import { useMemo } from "react";
import { useLocalStorage } from "./useLocalStorage";
import {
  MODULES,
  ALL_LESSONS,
  TOTAL_XP,
  TOTAL_LESSONS,
  QUIZ,
  QUIZ_PASS_COUNT,
  getLesson,
  type ModuleId,
} from "@/lib/curriculum";
import { BADGES, type Badge, type BadgeContext } from "@/lib/badges";

export interface ProgressState {
  /** lessonId -> chosen choice id */
  completedLessons: Record<string, string>;
  /** when the curriculum was fully completed (ms epoch), if ever */
  completedAt: number | null;
  /** name to print on the certificate */
  learnerName: string;
  /** best final-quiz result: number of correct answers, or null if not taken */
  quizBestScore: number | null;
  /** whether the final quiz has been passed */
  quizPassed: boolean;
  /** current daily learning streak (consecutive days with activity) */
  streak: number;
  /** longest streak ever reached */
  longestStreak: number;
  /** the last local day (YYYY-MM-DD) the learner was active, or null */
  lastActiveDay: string | null;
}

const DEFAULT: ProgressState = {
  completedLessons: {},
  completedAt: null,
  learnerName: "",
  quizBestScore: null,
  quizPassed: false,
  streak: 0,
  longestStreak: 0,
  lastActiveDay: null,
};

/** Local calendar day key, e.g. "2026-09-09". */
function dayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Whole-day difference between two day keys (b - a). */
function daysBetween(a: string, b: string): number {
  const [ay, am, ad] = a.split("-").map(Number);
  const [by, bm, bd] = b.split("-").map(Number);
  const da = Date.UTC(ay, am - 1, ad);
  const db = Date.UTC(by, bm - 1, bd);
  return Math.round((db - da) / 86400000);
}

export function useProgress() {
  const [state, setState] = useLocalStorage<ProgressState>(
    "bch-school:progress",
    DEFAULT,
  );

  const completedIds = useMemo(
    () => Object.keys(state.completedLessons),
    [state.completedLessons],
  );

  const xp = useMemo(
    () =>
      completedIds.reduce((sum, id) => {
        const lesson = getLesson(id);
        return sum + (lesson?.xp ?? 0);
      }, 0),
    [completedIds],
  );

  const moduleCompletion = useMemo(() => {
    const out = {} as Record<ModuleId, { done: number; total: number }>;
    for (const m of MODULES) {
      const done = m.lessons.filter((l) => state.completedLessons[l.id]).length;
      out[m.id] = { done, total: m.lessons.length };
    }
    return out;
  }, [state.completedLessons]);

  // The first lesson (in curriculum order) the learner hasn't completed yet.
  // Undefined when every lesson is done. Powers "continue where you left off".
  const nextLesson = useMemo(
    () => ALL_LESSONS.find((l) => !state.completedLessons[l.id]),
    [state.completedLessons],
  );

  // The stored streak is only "live" if the learner was active today or
  // yesterday. If two or more days have passed, the streak has lapsed and we
  // display 0 (it will restart at 1 on the next completed lesson).
  const currentStreak = useMemo(() => {
    if (!state.lastActiveDay || state.streak === 0) return 0;
    const gap = daysBetween(state.lastActiveDay, dayKey(new Date()));
    return gap <= 1 ? state.streak : 0;
  }, [state.lastActiveDay, state.streak]);

  // True when the learner has already completed a lesson today.
  const activeToday = state.lastActiveDay === dayKey(new Date());

  const allLessonsComplete =
    completedIds.length >= TOTAL_LESSONS && TOTAL_LESSONS > 0;

  const quizPassed = state.quizPassed;

  // The full program is complete only when every lesson is done AND the quiz
  // has been passed. The certificate is gated on this.
  const allComplete = allLessonsComplete && quizPassed;

  const badgeCtx: BadgeContext = useMemo(
    () => ({
      completedLessons: completedIds,
      xp,
      moduleCompletion,
      allComplete,
    }),
    [completedIds, xp, moduleCompletion, allComplete],
  );

  const badges = useMemo(
    () =>
      BADGES.map((b: Badge) => ({
        ...b,
        unlocked: b.earned(badgeCtx),
      })),
    [badgeCtx],
  );

  const earnedBadges = useMemo(() => badges.filter((b) => b.unlocked), [badges]);

  function completeLesson(lessonId: string, choiceId: string) {
    setState((prev) => {
      const today = dayKey(new Date());
      let { streak, longestStreak, lastActiveDay } = prev;

      if (lastActiveDay !== today) {
        // First activity today — update the streak.
        const gap = lastActiveDay ? daysBetween(lastActiveDay, today) : null;
        if (gap === 1) {
          streak = streak + 1; // consecutive day
        } else {
          streak = 1; // first day, or streak was broken
        }
        lastActiveDay = today;
        longestStreak = Math.max(longestStreak, streak);
      }

      return {
        ...prev,
        completedLessons: { ...prev.completedLessons, [lessonId]: choiceId },
        streak,
        longestStreak,
        lastActiveDay,
      };
    });
  }

  /**
   * Record a final-quiz attempt. Stores the best score and, on a pass,
   * marks the whole program complete (stamping the certificate date).
   */
  function submitQuiz(score: number) {
    const passed = score >= QUIZ_PASS_COUNT;
    setState((prev) => {
      const bestScore =
        prev.quizBestScore === null
          ? score
          : Math.max(prev.quizBestScore, score);
      const nowPassed = prev.quizPassed || passed;
      const lessonsDone =
        Object.keys(prev.completedLessons).length >= TOTAL_LESSONS;
      return {
        ...prev,
        quizBestScore: bestScore,
        quizPassed: nowPassed,
        completedAt:
          nowPassed && lessonsDone && !prev.completedAt
            ? Date.now()
            : prev.completedAt,
      };
    });
    return passed;
  }

  function isLessonComplete(lessonId: string) {
    return Boolean(state.completedLessons[lessonId]);
  }

  function setLearnerName(name: string) {
    setState((prev) => ({ ...prev, learnerName: name }));
  }

  function resetProgress() {
    setState(DEFAULT);
  }

  return {
    state,
    xp,
    totalXp: TOTAL_XP,
    completedCount: completedIds.length,
    totalLessons: TOTAL_LESSONS,
    moduleCompletion,
    nextLesson,
    currentStreak,
    longestStreak: state.longestStreak,
    activeToday,
    allComplete,
    allLessonsComplete,
    quizPassed,
    quizBestScore: state.quizBestScore,
    quizTotal: QUIZ.length,
    badges,
    earnedBadges,
    completeLesson,
    isLessonComplete,
    submitQuiz,
    setLearnerName,
    resetProgress,
    percent: TOTAL_LESSONS
      ? Math.round((completedIds.length / TOTAL_LESSONS) * 100)
      : 0,
  };
}

export const _allLessons = ALL_LESSONS;
